import React, { useState } from "react";
import { signInWithGoogle, signInWithEmail } from "@/lib/supabase";
import { Loader2 } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

export default function Login() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [mode, setMode] = useState("signin");   // "signin" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function go() {
    setBusy(true); setError("");
    const { error } = await signInWithGoogle();   // redirects away on success
    if (error) { setError(error.message || "Sign-in failed. Try again."); setBusy(false); }
  }

  // No navigate() on success: App.js watches the auth state, and /login redirects to /dashboard the
  // moment a session exists — the same path the Google redirect lands on.
  async function goEmail(e) {
    e.preventDefault();
    setBusy(true); setError(""); setNotice("");
    const { data, error } = await signInWithEmail(email.trim(), password, mode);
    if (error) { setError(error.message || "Sign-in failed. Try again."); setBusy(false); return; }
    // Sign-up with email confirmation on returns a user but no session — say so rather than hanging.
    if (mode === "signup" && !data?.session) {
      setNotice("Check your inbox to confirm your address, then sign in.");
      setMode("signin");
    }
    setBusy(false);
  }

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-6">
      <div className="absolute top-4 right-4"><ThemeToggle /></div>

      <div className="w-full max-w-[340px]">
        <div className="mb-8">
          <p className="text-xs font-semibold tracking-[0.18em] text-ink mb-6">STRIDE</p>
          <h1 className="text-h1 hd">Your job search, executed</h1>
          <p className="text-base text-ink-2 mt-2">Sign in to pick up your plan where you left off.</p>
        </div>

        <button
          onClick={go}
          disabled={busy}
          className="w-full flex items-center justify-center gap-2.5 py-2.5 rounded-md border border-line bg-surface text-sm font-medium text-ink hover:border-line-strong disabled:opacity-60 disabled:cursor-default cursor-pointer"
        >
          {busy ? <Loader2 size={15} className="animate-spin text-ink-3" /> : <GoogleMark />}
          {busy ? "Redirecting…" : "Continue with Google"}
        </button>

        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-line" />
          <span className="text-2xs text-ink-3">or</span>
          <div className="h-px flex-1 bg-line" />
        </div>

        <form onSubmit={goEmail} className="flex flex-col gap-2.5">
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@gmail.com" autoComplete="email"
            className="w-full px-3 py-2.5 rounded-md border border-line bg-elevated text-sm text-ink placeholder:text-ink-3 focus:border-line-strong outline-none"
          />
          <input
            type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" autoComplete={mode === "signup" ? "new-password" : "current-password"}
            className="w-full px-3 py-2.5 rounded-md border border-line bg-elevated text-sm text-ink placeholder:text-ink-3 focus:border-line-strong outline-none"
          />
          <button
            type="submit" disabled={busy}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-accent text-accent-fg text-sm font-semibold hover:bg-accent-hover disabled:opacity-60 disabled:cursor-default cursor-pointer"
          >
            {busy && <Loader2 size={15} className="animate-spin" />}
            {mode === "signup" ? "Create account" : "Sign in"}
          </button>
        </form>

        <button
          type="button"
          onClick={() => { setMode(mode === "signup" ? "signin" : "signup"); setError(""); setNotice(""); }}
          className="text-xs text-ink-2 hover:text-ink mt-3 cursor-pointer bg-transparent border-none p-0"
        >
          {mode === "signup" ? "Already have an account? Sign in" : "New here? Create an account"}
        </button>

        {error && (
          <p role="alert" className="text-xs text-danger mt-3">{error}</p>
        )}
        {notice && (
          <p role="status" className="text-xs text-success mt-3">{notice}</p>
        )}

        <p className="text-xs text-ink-3 mt-6 leading-relaxed">
          By continuing you agree to keep grinding. We only use your account to sign you in.
        </p>
      </div>
    </div>
  );
}
